import React, { useState, useEffect, useCallback } from 'react'
import { 
  FormComponentType, 
  IFormControlChange, 
  IFormValidationErrors
} from './df-form-controls/df-form-preview-interfaces'
import { conditionalLogicService } from './services/conditionalLogicService'
import { FormComponentType as TFormComponent } from './df-form-controls/df-form-preview-interfaces'
import { toastService } from './services/toast.service'

// Import form control components
import { DfFormInput } from './df-form-controls/df-form-input'
import { DfFormTextarea } from './df-form-controls/df-form-textarea'
import { DfFormSelect } from './df-form-controls/df-form-select'
import { DfFormCheckbox } from './df-form-controls/df-form-checkbox'
import { DfFormRadio } from './df-form-controls/df-form-radio'
import { DfFormSegment } from './df-form-controls/df-form-segment'
import { DfFormDateTime } from './df-form-controls/df-form-date-time'
import { DfFormSignature } from './df-form-controls/df-form-signature'
import { DfFormFileUpload } from './df-form-controls/df-form-file-upload'
import { DfFormLocation } from './df-form-controls/df-form-location'
import { DfFormHeading } from './df-form-controls/df-form-heading'
import { DfFormInstruction } from './df-form-controls/df-form-instruction'
import { DfFormSection } from './df-form-controls/df-form-section/df-form-section'
import { DfFormDataGrid } from './df-form-controls/df-form-grid/df-form-grid'
import { ComponentActionFeatures } from './ComponentActionFeatures'
import { ComponentSubmissionActions } from './components/ComponentSubmissionActions'

// Import styles
import './DfFormPreview.scss'

// Dynamic imports to avoid circular dependencies
const DfFormTable = React.lazy(() => import('./df-form-controls/df-form-table/df-form-table'))

export type DeviceType = 'desktop' | 'tablet' | 'mobile'

export interface DfFormPreviewProps {
  formComponents: FormComponentType[]
  formData?: any
  currentDevice?: DeviceType
  isPreviewMode?: boolean
  initialFormData?: FormComponentType[]
  onSubmit?: (formData: FormComponentType[]) => void
  onFormDataChange?: (formData: FormComponentType[]) => void
  formTitle?: string
  formDescription?: string
  formTemplateId?: string
  // Optional component management props for edit mode
  onComponentSelect?: (component: FormComponentType) => void
  onComponentDelete?: (component: FormComponentType, event: React.MouseEvent) => void
  onComponentEdit?: (component: FormComponentType) => void
  onComponentUpdate?: (componentId: string, updates: Partial<FormComponentType>) => void
  selectedComponent?: FormComponentType | null
}

export const DfFormPreview: React.FC<DfFormPreviewProps> = ({
  formComponents = [],
  currentDevice = 'desktop',
  isPreviewMode = false,
  initialFormData = [],
  onSubmit,
  onFormDataChange,
  formTitle,
  formDescription,
  formTemplateId,
  // Add component management props for edit mode
  onComponentSelect,
  onComponentDelete,
  onComponentEdit,
  onComponentUpdate,
  selectedComponent
}) => {
  // Form state
  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const [validationErrors, setValidationErrors] = useState<IFormValidationErrors>({})
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({})
  const [componentVisibility, setComponentVisibility] = useState<Record<string, boolean>>({})
  // Track raised issues for threshold conditions (Set of condition IDs)
  const [raisedThresholdIssues, setRaisedThresholdIssues] = useState<Set<string>>(new Set())
  // Track threshold action completions: Map<conditionId, { notesCompleted, attachmentsCompleted, emailSent }>
  const [thresholdActionCompletions, setThresholdActionCompletions] = useState<Map<string, { notesCompleted: boolean; attachmentsCompleted: boolean; emailSent: boolean }>>(new Map())
  // Track threshold validation state for submit button
  const [thresholdValidationState, setThresholdValidationState] = useState<{ isValid: boolean; errorMessage?: string }>({ isValid: true })

  // Initialize form state
  useEffect(() => {
    initializeFormState()
  }, [formComponents, initialFormData])

  // Recursive function to initialize values from nested components
  const initializeComponentValues = (components: any[], values: Record<string, any>) => {
    components.forEach(component => {
      if (component.id) {
        // Always initialize component in form state, even with empty values
        // This ensures that value changes are tracked properly
        if ('value' in component.basic && component.basic.value !== undefined) {
          values[component.id] = component.basic.value
        }
        // Fallback to defaultValue if no value
        else if ('defaultValue' in component.basic && component.basic.defaultValue !== undefined) {
          values[component.id] = component.basic.defaultValue
        }
        // If neither value nor defaultValue exists, initialize with appropriate default based on component type
        else {
          // For checkbox and multi-select components, initialize with empty array
          if (component.name === 'checkbox' || component.name === 'select') {
            values[component.id] = []
          } else {
            // For other components, initialize with empty string
            values[component.id] = ''
          }
        }
      }

      // Handle nested components in table cells
      if (component.cells && Array.isArray(component.cells)) {
        component.cells.forEach((row: any[], _rowIndex: number) => {
          if (Array.isArray(row)) {
            row.forEach((cell: any, _cellIndex: number) => {
              if (cell && cell.components && Array.isArray(cell.components)) {
                initializeComponentValues(cell.components, values)
              }
            })
          }
        })
      }

      // Handle nested components in datagrid entries
      if (component.entries && Array.isArray(component.entries)) {
        component.entries.forEach((entry: any, _entryIndex: number) => {
          if (entry && entry.components && Array.isArray(entry.components)) {
            initializeComponentValues(entry.components, values)
          }
        })
      }
      
      // Handle file component - initialize with file data if present
      if (component.name === 'file' && component.basic) {
        const fileData = (component.basic as any).files || (component.basic as any).attachments || (component.basic as any).value;
        if (fileData) {
          values[component.id] = fileData;
        }
      }
      
      // Handle instructions component - ensure instructions array exists
      if (component.name === 'instructions' && component.basic) {
        if (!(component.basic as any).instructions) {
          // Initialize empty instructions array if not present
          (component.basic as any).instructions = [];
        }
      }

      // Handle nested components in section children
      if (component.children && Array.isArray(component.children)) {
        initializeComponentValues(component.children, values)
      }
    })
  }

  const initializeFormState = useCallback(() => {
    const initialValues: Record<string, any> = {}
    const seenIds = new Set<string>();
    
    // Validate component IDs for uniqueness
    const validateComponentIds = (components: any[]): void => {
      components.forEach(component => {
        if (component.id) {
          if (seenIds.has(component.id)) {
            console.error(`[DfFormPreview] Duplicate component ID detected: ${component.id}. Component:`, component);
            // Generate a unique ID for duplicate
            component.id = `${component.id}-duplicate-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            console.warn(`[DfFormPreview] Generated new unique ID: ${component.id}`);
          }
          seenIds.add(component.id);
        }
        
        // Recursively validate nested components
        if (component.children && Array.isArray(component.children)) {
          validateComponentIds(component.children);
        }
        if (component.cells && Array.isArray(component.cells)) {
          component.cells.forEach((row: any[]) => {
            if (Array.isArray(row)) {
              row.forEach((cell: any) => {
                if (cell && cell.components && Array.isArray(cell.components)) {
                  validateComponentIds(cell.components);
                }
              });
            }
          });
        }
        if (component.entries && Array.isArray(component.entries)) {
          component.entries.forEach((entry: any) => {
            if (entry && entry.components && Array.isArray(entry.components)) {
              validateComponentIds(entry.components);
            }
          });
        }
      });
    };
    
    // Validate all component IDs for uniqueness
    if (formComponents && formComponents.length > 0) {
      validateComponentIds(formComponents);
    }
    if (initialFormData && initialFormData.length > 0) {
      validateComponentIds(initialFormData);
    }
    
    // Initialize form values from initial data
    if (initialFormData && initialFormData.length > 0) {
      initializeComponentValues(initialFormData, initialValues)
    }
    
    // Initialize form values from form components (for submitted data)
    if (formComponents && formComponents.length > 0) {
      initializeComponentValues(formComponents, initialValues)
    }
    setFormValues(initialValues)

    // Evaluate initial conditional logic
    evaluateConditionalLogic()
  }, [initialFormData, formComponents])

  // Recursive function to evaluate conditional logic for nested components
  const evaluateComponentConditionalLogic = (components: any[], visibility: Record<string, boolean>) => {
    components.forEach(component => {
      if (component.id) {
        // Skip conditional logic for table and datagrid components - they are always visible
        if (component.name === 'table' || component.name === 'datagrid') {
          visibility[component.id] = true
        } else if (component.conditional) {
          // Use the proper conditional logic service
          const result = conditionalLogicService.evaluateConditionalLogic(
            component.conditional,
            formComponents,
            formValues
          )
          visibility[component.id] = result.shouldShow
        } else {
          // No conditional logic means always show
          visibility[component.id] = true
        }
      }

      // Handle nested components in table cells
      if (component.cells && Array.isArray(component.cells)) {
        component.cells.forEach((row: any[]) => {
          row.forEach((cell: any) => {
            if (cell.components && Array.isArray(cell.components)) {
              evaluateComponentConditionalLogic(cell.components, visibility)
            }
          })
        })
      }

      // Handle nested components in datagrid entries
      if (component.entries && Array.isArray(component.entries)) {
        component.entries.forEach((entry: any) => {
          if (entry.components && Array.isArray(entry.components)) {
            evaluateComponentConditionalLogic(entry.components, visibility)
          }
        })
      }

      // Handle nested components in section children
      if (component.children && Array.isArray(component.children)) {
        evaluateComponentConditionalLogic(component.children, visibility)
      }
    })
  }

  // Evaluate conditional logic for all components
  const evaluateConditionalLogic = useCallback(() => {
    const visibility: Record<string, boolean> = {}
    
    if (formComponents && formComponents.length > 0) {
      evaluateComponentConditionalLogic(formComponents, visibility)
    }
    
    setComponentVisibility(visibility)
  }, [formComponents, formValues])

  // Check if a component should be visible based on conditional logic
  const shouldShowComponent = useCallback((componentId: string): boolean => {
    // Find the component to check its type
    const component = formComponents.find(comp => comp.id === componentId)
    
    // Table and datagrid components are always visible (no conditional logic)
    if (component && (component.name === 'table' || component.name === 'datagrid')) {
      return true
    }
    
    const isVisible = componentVisibility[componentId] !== false
    return isVisible
  }, [componentVisibility, formComponents])

  // Handle form value changes and re-evaluate conditional logic
  const onFormValueChange = useCallback((change: IFormControlChange) => {
    // CRITICAL: Validate that change.id is valid and unique
    if (!change.id || typeof change.id !== 'string' || change.id.trim() === '') {
      console.error('[DfFormPreview] Invalid component ID in onFormValueChange:', change);
      return;
    }
    
    // CRITICAL: Check if multiple components share this ID (ID collision detection)
    const componentsWithSameId = formComponents.filter(comp => comp.id === change.id);
    if (componentsWithSameId.length > 1) {
      console.error(`[DfFormPreview] ID COLLISION DETECTED! Multiple components share ID "${change.id}":`, 
        componentsWithSameId.map(c => ({ id: c.id, name: c.name, label: c.basic?.label })));
      // Don't update - this would cause all components with this ID to get the same value
      return;
    }
    
    // Debug: Log value changes to identify ID collisions
    const existingValue = formValues[change.id];
    if (existingValue !== undefined && existingValue !== change.value) {
      console.log('[DfFormPreview] Updating form value:', {
        id: change.id,
        oldValue: existingValue,
        newValue: change.value
      });
    }
    
    const newFormValues = {
      ...formValues,
      [change.id]: change.value
    }
    setFormValues(newFormValues)
    
    // Clear raised issues for this component when value changes
    // This ensures that if threshold condition changes, user must raise issue again
    const component = formComponents.find(comp => comp.id === change.id);
    if (component) {
      const threshold = (component as any)?.threshold;
      if (threshold && threshold.conditions && threshold.conditions.length > 0) {
        setRaisedThresholdIssues(prev => {
          const newSet = new Set(prev);
          threshold.conditions.forEach((condition: any) => {
            newSet.delete(condition.id);
          });
          return newSet;
        });
      }
    }
    
    // Recursive function to update component values
    const updateComponentValue = (components: FormComponentType[]): FormComponentType[] => {
      return components.map(component => {
        if (component.id === change.id) {
          if ('defaultValue' in component.basic) {
            return {
              ...component,
              basic: {
                ...component.basic,
                value: change.value ?? component.basic.defaultValue,
                defaultValue: change.value
              }
            } as FormComponentType
          }
        }
        
        // Recursively update nested components
        if (component.name === 'section' && (component as any).children) {
          return {
            ...component,
            children: updateComponentValue((component as any).children)
          } as FormComponentType
        }
        
        if (component.name === 'table' && (component as any).cells) {
          return {
            ...component,
            cells: (component as any).cells.map((row: any[]) =>
              row.map((cell: any) => {
                const updatedCell = { ...cell };
                if (updatedCell.components) {
                  updatedCell.components = updateComponentValue(updatedCell.components);
                }
                return updatedCell;
              })
            )
          } as FormComponentType
        }
        
        if (component.name === 'datagrid' && (component as any).entries) {
          return {
            ...component,
            entries: (component as any).entries.map((entry: any) => {
              const updatedEntry = { ...entry };
              if (updatedEntry.components) {
                updatedEntry.components = updateComponentValue(updatedEntry.components);
              }
              return updatedEntry;
            })
          } as FormComponentType
        }
        
        return component
      })
    }
    
    // Update form components
    const updatedComponents = updateComponentValue(formComponents)
    
    onFormDataChange?.(updatedComponents)
    
    // Clear validation errors when user starts typing and re-validate
    if (validationErrors[change.id]) {
      setValidationErrors(prev => ({
        ...prev,
        [change.id]: ''
      }))
    }
    
    // Re-validate the field after value change (only in test mode)
    if (!isPreviewMode) {
      setTimeout(() => {
        validateField(change.id)
      }, 100)
    }
    
    // Re-evaluate conditional logic with new form values
    setTimeout(() => {
      const visibility: Record<string, boolean> = {}
      
      formComponents.forEach(component => {
        // Skip conditional logic for table and datagrid components - they are always visible
        if (component.name === 'table' || component.name === 'datagrid') {
          visibility[component.id] = true
        } else if (component.conditional) {
          const result = conditionalLogicService.evaluateConditionalLogic(
            component.conditional,
            formComponents,
            newFormValues
          )
          visibility[component.id] = result.shouldShow
        } else {
          visibility[component.id] = true
        }
      })
      
      setComponentVisibility(visibility)
    }, 0)
  }, [formComponents, formValues, validationErrors, onFormDataChange])

  // Recursive function to find a component by ID in nested structures
  const findComponentById = useCallback((components: FormComponentType[], fieldId: string): FormComponentType | null => {
    for (const comp of components) {
      if (comp.id === fieldId) {
        return comp
      }
      
      // Check nested components in sections
      if (comp.name === 'section' && (comp as any).children) {
        const found = findComponentById((comp as any).children, fieldId)
        if (found) return found
      }
      
      // Check nested components in table cells
      if (comp.name === 'table' && (comp as any).cells) {
        for (const row of (comp as any).cells) {
          for (const cell of row) {
            if (cell.components) {
              const found = findComponentById(cell.components, fieldId)
              if (found) return found
            }
          }
        }
      }
      
      // Check nested components in datagrid entries
      if (comp.name === 'datagrid' && (comp as any).entries) {
        for (const entry of (comp as any).entries) {
          if (entry.components) {
            const found = findComponentById(entry.components, fieldId)
            if (found) return found
          }
        }
      }
    }
    return null
  }, [])

  // Validate individual field
  const validateField = useCallback((fieldId: string) => {
    // Only validate in test mode
    if (isPreviewMode) return
    
    // Find component recursively (handles nested components)
    const component = findComponentById(formComponents, fieldId)
    if (!component) return

    // Get current value from form values (real-time input)
    const currentValue = formValues[fieldId] || ''
    
    // Check if field has a value
    let hasValue = false
    
    if (Array.isArray(currentValue)) {
      // For array values (checkbox, multi-select), check if array has items
      hasValue = currentValue.length > 0
    } else if (typeof currentValue === 'string') {
      // For string values, check if not empty after trimming
      hasValue = currentValue.trim() !== ''
    } else {
      // For other types, check if not null/undefined
      hasValue = currentValue !== null && currentValue !== undefined
    }

    let errorMessage = ''

    // Check for required validation
    if (component.validation.required && !hasValue) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} is required`
    }
    // Check for email validation
    else if (component.name === 'email-input' && hasValue && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(currentValue)) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} must be a valid email`
    }
    // Check for minLength validation
    else if (component.validation.minLength && hasValue && currentValue.length < component.validation.minLength) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} must be at least ${component.validation.minLength} characters`
    }
    // Check for maxLength validation
    else if (component.validation.maxLength && hasValue && currentValue.length > component.validation.maxLength) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} must be no more than ${component.validation.maxLength} characters`
    }
    // Check for number validations
    else if (component.name === 'number-input' && hasValue) {
      const numValue = parseFloat(String(currentValue))
      if (isNaN(numValue)) {
        const customMessage = component.validation.customValidationMessage
        const fieldLabel = component.basic.label || 'This field'
        errorMessage = customMessage || `${fieldLabel} must be a valid number`
      } else {
        const numberValidation = component.validation as any
        if (numberValidation.lowerLimit !== undefined && numValue < numberValidation.lowerLimit) {
          const customMessage = component.validation.customValidationMessage
          const fieldLabel = component.basic.label || 'This field'
          errorMessage = customMessage || `You are under the limit. ${fieldLabel} must be at least ${numberValidation.lowerLimit}`
        } else if (numberValidation.upperLimit !== undefined && numValue > numberValidation.upperLimit) {
          const customMessage = component.validation.customValidationMessage
          const fieldLabel = component.basic.label || 'This field'
          errorMessage = customMessage || `You crossed the limit. ${fieldLabel} must be no more than ${numberValidation.upperLimit}`
        }
      }
    }
    
    // Always set the error message (empty string if valid) to clear previous errors
    setValidationErrors(prev => ({
      ...prev,
      [fieldId]: errorMessage
    }))
  }, [formComponents, isPreviewMode, formValues, findComponentById])

  // Handle form control blur
  const onFormControlBlur = useCallback((fieldId: string) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldId]: true
    }))
    // Validate field on blur (only in test mode)
    if (!isPreviewMode) {
      // Use setTimeout to ensure formValues are updated
      setTimeout(() => {
        validateField(fieldId)
      }, 0)
    }
  }, [isPreviewMode, validateField])

  // Handle form control focus
  const onFormControlFocus = useCallback((fieldId: string) => {
    // Clear validation errors when field is focused
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldId]: ''
      }))
    }
  }, [validationErrors])

  // Disabled automatic validation to let DfFormInput handle its own validation
  // This prevents conflicts between external and local validation
  // useEffect(() => {
  //   if (!isPreviewMode && Object.keys(formValues).length > 0) {
  //     // Validate all form components when form values change (but not on initial load)
  //     formComponents.forEach(component => {
  //       validateField(component.id)
  //     })
  //   }
  // }, [formValues, validateField, isPreviewMode, formComponents])

  // Enhanced form validity check
  const isFormValid = useCallback((): boolean => {
    return formComponents.every(component => {
      // Use real-time form values instead of component values
      const currentValue = formValues[component.id] || ''
      
      // Check if field has a value
      let hasValue = false
      
      if (Array.isArray(currentValue)) {
        // For array values (checkbox, multi-select), check if array has items
        hasValue = currentValue.length > 0
      } else if (typeof currentValue === 'string') {
        // For string values, check if not empty after trimming
        hasValue = currentValue.trim() !== ''
      } else {
        // For other types, check if not null/undefined
        hasValue = currentValue !== null && currentValue !== undefined
      }
      
      if (component.validation.required && !hasValue) {
        return false
      }
      return true
    })
  }, [formComponents, formValues])

  // Evaluate threshold condition (handles both numeric and string values)
  const evaluateThresholdCondition = useCallback((condition: any, currentVal: string | number | string[]): boolean => {
    // Handle array values (for checkbox)
    if (Array.isArray(currentVal)) {
      return currentVal.some(val => evaluateThresholdCondition(condition, val));
    }
    
    if (currentVal === undefined || currentVal === null || currentVal === '') return false;
    
    // Try numeric comparison first
    const numValue = typeof currentVal === 'string' ? parseFloat(currentVal) : currentVal;
    const thresholdValue = typeof condition.value === 'string' ? parseFloat(condition.value) : condition.value;
    
    // If both are valid numbers, use numeric comparison
    if (!isNaN(numValue) && !isNaN(thresholdValue)) {
      switch (condition.operator) {
        case 'greaterThan':
          return numValue > thresholdValue;
        case 'lessThan':
          return numValue < thresholdValue;
        case 'greaterThanOrEqual':
          return numValue >= thresholdValue;
        case 'lessThanOrEqual':
          return numValue <= thresholdValue;
        case 'equals':
          return numValue === thresholdValue;
        case 'notEquals':
          return numValue !== thresholdValue;
        default:
          return false;
      }
    }
    
    // For string comparison (checkbox, radio, select, segment)
    const stringValue = String(currentVal).toLowerCase();
    const stringConditionValue = String(condition.value).toLowerCase();
    
    switch (condition.operator) {
      case 'equals':
        return stringValue === stringConditionValue || String(currentVal) === String(condition.value);
      case 'notEquals':
        return stringValue !== stringConditionValue && String(currentVal) !== String(condition.value);
      case 'greaterThan':
      case 'lessThan':
      case 'greaterThanOrEqual':
      case 'lessThanOrEqual':
        // For string values, these operators don't make sense, return false
        return false;
      default:
        return false;
    }
  }, []);

  // Get all met threshold conditions that require issue to be raised
  const getMetThresholdConditions = useCallback((): Array<{ componentId: string; condition: any; componentLabel: string }> => {
    if (isPreviewMode) return [];
    
    const metConditions: Array<{ componentId: string; condition: any; componentLabel: string }> = [];
    
    formComponents.forEach(component => {
      const condition = (component as any)?.condition;
      if (!condition || !condition.conditions || condition.conditions.length === 0) {
        return;
      }
      
      const currentValue = formValues[component.id];
      // Check if value is valid (not undefined, null, empty string, or empty array)
      const hasValidValue = currentValue !== undefined && 
                            currentValue !== null && 
                            currentValue !== '' && 
                            !(Array.isArray(currentValue) && currentValue.length === 0);
      
      if (!hasValidValue) {
        return;
      }
      
      condition.conditions.forEach((cond: any) => {
        // All met threshold conditions require raising an issue (mandatory)
        if (evaluateThresholdCondition(cond, currentValue)) {
          metConditions.push({
            componentId: component.id,
            condition: cond,
            componentLabel: component.basic?.label || 'Field'
          });
        }
      });
    });
    
    return metConditions;
  }, [formComponents, formValues, isPreviewMode, evaluateThresholdCondition]);

  // Validate threshold conditions - check if all met conditions have all mandatory actions completed
  const validateThresholdConditions = useCallback((): { isValid: boolean; errorMessage?: string } => {
    if (isPreviewMode) return { isValid: true };
    
    const metConditions = getMetThresholdConditions();
    
    if (metConditions.length === 0) {
      return { isValid: true };
    }
    
    // Check each met condition for all mandatory actions
    for (const { condition, componentLabel } of metConditions) {
      const completions = thresholdActionCompletions.get(condition.id) || { notesCompleted: false, attachmentsCompleted: false, emailSent: false };
      const missingActions: string[] = [];
      
      // Check if raise issue is required and completed
      if (condition.enableRaiseIssue === true && !raisedThresholdIssues.has(condition.id)) {
        missingActions.push('raise an issue');
      }
      
      // Check if notes are required and completed
      if (condition.enableNotes === true && !completions.notesCompleted) {
        missingActions.push('add notes');
      }
      
      // Check if attachments are required and completed
      if (condition.enableAttachment === true && !completions.attachmentsCompleted) {
        missingActions.push('add attachments');
      }
      
      // Check if send email is required and completed
      if (condition.enableSendEmail === true && !completions.emailSent) {
        missingActions.push('send email');
      }
      
      if (missingActions.length > 0) {
        const actionsText = missingActions.length === 1 
          ? missingActions[0] 
          : missingActions.slice(0, -1).join(', ') + ' and ' + missingActions[missingActions.length - 1];
        return {
          isValid: false,
          errorMessage: `Please ${actionsText} for the threshold condition met in "${componentLabel}". You cannot submit the form until all required actions are completed.`
        };
      }
    }
    
    return { isValid: true };
  }, [isPreviewMode, getMetThresholdConditions, raisedThresholdIssues, thresholdActionCompletions]);

  // Callback to mark threshold issue as raised
  const handleThresholdIssueRaised = useCallback((conditionId: string) => {
    if (conditionId) {
      setRaisedThresholdIssues(prev => {
        const newSet = new Set(prev);
        newSet.add(conditionId);
        return newSet;
      });
    }
  }, []);

  // Callback to update threshold action completions
  const handleThresholdActionCompletion = useCallback((conditionId: string, action: 'notes' | 'attachments' | 'email', completed: boolean) => {
    if (conditionId) {
      setThresholdActionCompletions(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(conditionId) || { notesCompleted: false, attachmentsCompleted: false, emailSent: false };
        if (action === 'notes') {
          newMap.set(conditionId, { ...current, notesCompleted: completed });
        } else if (action === 'attachments') {
          newMap.set(conditionId, { ...current, attachmentsCompleted: completed });
        } else if (action === 'email') {
          newMap.set(conditionId, { ...current, emailSent: completed });
        }
        return newMap;
      });
    }
  }, []);

  // Update threshold validation state when form values, raised issues, or action completions change
  useEffect(() => {
    if (!isPreviewMode) {
      const validation = validateThresholdConditions();
      setThresholdValidationState(validation);
    }
  }, [formValues, raisedThresholdIssues, thresholdActionCompletions, isPreviewMode, validateThresholdConditions]);

  // Validate all fields
  const validateAllFields = useCallback((): boolean => {
    // Only validate in test mode
    if (isPreviewMode) return true
    
    let isValid = true
    setFormSubmitted(true)
    const errors: IFormValidationErrors = {}

    formComponents.forEach(component => {
      // Use real-time form values instead of component values
      const currentValue = formValues[component.id] || ''
      
      // Check if field has a value
      let hasValue = false
      
      if (Array.isArray(currentValue)) {
        // For array values (checkbox, multi-select), check if array has items
        hasValue = currentValue.length > 0
      } else if (typeof currentValue === 'string') {
        // For string values, check if not empty after trimming
        hasValue = currentValue.trim() !== ''
      } else {
        // For other types, check if not null/undefined
        hasValue = currentValue !== null && currentValue !== undefined
      }

      if (component.validation.required && !hasValue) {
        const customMessage = component.validation.customValidationMessage
        const fieldLabel = component.basic.label || 'This field'
        const errorMessage = customMessage || `${fieldLabel} is required`
        errors[component.id] = errorMessage
        isValid = false
      } else {
        errors[component.id] = ''
      }

      setTouchedFields(prev => ({
        ...prev,
        [component.id]: true
      }))
    })

    setValidationErrors(errors)
    return isValid
  }, [formComponents, isPreviewMode, formValues])

  // Handle form submission
  const handleSubmit = useCallback(() => {
    setFormSubmitted(true)

    if (!validateAllFields()) {
      // Focus on the first invalid field
      const firstErrorField = formComponents.find(comp => {
        const currentValue = formValues[comp.id] || ''
        
        // Check if field has a value
        let hasValue = false
        
        if (Array.isArray(currentValue)) {
          // For array values (checkbox, multi-select), check if array has items
          hasValue = currentValue.length > 0
        } else if (typeof currentValue === 'string') {
          // For string values, check if not empty after trimming
          hasValue = currentValue.trim() !== ''
        } else {
          // For other types, check if not null/undefined
          hasValue = currentValue !== null && currentValue !== undefined
        }
        
        return comp.validation.required && !hasValue
      })
      if (firstErrorField) {
        const errorElement = document.getElementById(firstErrorField.id)
        if (errorElement) {
          errorElement.focus()
        }
      }
      return
    }

    // Validate threshold conditions - ensure all met conditions have issues raised
    const thresholdValidation = validateThresholdConditions();
    if (!thresholdValidation.isValid) {
      // Show error message
      if (thresholdValidation.errorMessage) {
        toastService.showError(thresholdValidation.errorMessage);
      }
      // Focus on the first component with unmet threshold condition
      const metConditions = getMetThresholdConditions();
      const firstUnmet = metConditions.find(
        ({ condition }) => !raisedThresholdIssues.has(condition.id)
      );
      if (firstUnmet) {
        const errorElement = document.getElementById(firstUnmet.componentId);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    // Recursive function to update component values from form state
    const updateComponentValues = (components: FormComponentType[]): FormComponentType[] => {
      return components.map(component => {
        const userValue = formValues[component.id]
        let finalValue = userValue
        
        // Debug: Log all form values to see what's being tracked
        if (component.name === 'text-input' && component.basic?.label === 'New text input') {
        }
        
        if (finalValue === undefined) {
          if ('value' in component.basic && component.basic.value !== undefined) {
            finalValue = component.basic.value
          } else if ('defaultValue' in component.basic && component.basic.defaultValue !== undefined) {
            finalValue = component.basic.defaultValue
          } else {
            finalValue = ''
          }
        }

        // Debug table cell components
        if (component.name === 'text-input' && component.basic?.label === 'New text input') {
        }

        const updatedComponent = {
          ...component,
          basic: {
            ...component.basic,
            value: finalValue,
            defaultValue: finalValue
          }
        } as FormComponentType

        // Recursively update nested components
        if (updatedComponent.name === 'section' && (updatedComponent as any).children) {
          (updatedComponent as any).children = updateComponentValues((updatedComponent as any).children);
        }
        
        if (updatedComponent.name === 'table' && (updatedComponent as any).cells) {
          (updatedComponent as any).cells = (updatedComponent as any).cells.map((row: any[]) =>
            row.map((cell: any) => {
              const updatedCell = { ...cell };
              if (updatedCell.components) {
                updatedCell.components = updateComponentValues(updatedCell.components);
              }
              return updatedCell;
            })
          );
        }
        
        if (updatedComponent.name === 'datagrid' && (updatedComponent as any).entries) {
          (updatedComponent as any).entries = (updatedComponent as any).entries.map((entry: any) => {
            const updatedEntry = { ...entry };
            if (updatedEntry.components) {
              updatedEntry.components = updateComponentValues(updatedEntry.components);
            }
            return updatedEntry;
          });
        }

        return updatedComponent;
      });
    };

    // Prepare final form data
    const finalFormData = updateComponentValues(formComponents)

    onSubmit?.(finalFormData)
  }, [formComponents, formValues, validateAllFields, onSubmit])

  // Get preview classes
  const getPreviewClasses = useCallback(() => {
    return {
      [`device-${currentDevice}`]: true,
      readonly: isPreviewMode
    }
  }, [currentDevice, isPreviewMode])

  // Render form component based on type
  const renderFormComponent = (component: FormComponentType) => {
    // CRITICAL: Ensure component has a valid unique ID
    if (!component.id || typeof component.id !== 'string' || component.id.trim() === '') {
      console.error('[DfFormPreview] Component missing ID:', component);
      // Generate a unique ID if missing
      component.id = `generated-${component.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.warn('[DfFormPreview] Generated ID for component:', component.id);
    }
    
    // CRITICAL: Use component.id directly - ensure it's the actual component's ID, not a shared reference
    const componentId = component.id;
    const formValue = formValues[componentId]
    
    // Debug: Log if formValue is being shared incorrectly
    if (formValue !== undefined) {
      // Check if this value is being used by multiple components
      const componentsWithSameValue = formComponents.filter(comp => 
        comp.id !== componentId && formValues[comp.id] === formValue
      );
      if (componentsWithSameValue.length > 0) {
        console.warn(`[DfFormPreview] Component ${componentId} shares form value with other components:`, 
          componentsWithSameValue.map(c => c.id));
      }
    }
    
    const commonProps = {
      id: componentId, // Use the validated component ID
      validationErrors,
      formValue: formValue, // This should be unique per component ID
      touchedFields,
      formSubmitted,
      mode: (isPreviewMode ? 'preview' : 'test') as 'preview' | 'test',
      readonly: isPreviewMode,
      onValueChange: onFormValueChange, // This will use change.id which should match componentId
      onBlur: () => onFormControlBlur(componentId),
      onFocus: () => onFormControlFocus(componentId)
    }

    switch (component.name) {
      case 'text-input':
      case 'number-input':
      case 'email-input':
        // Regular text/email/number input
        return (
          <DfFormInput
            {...commonProps}
            properties={component as any}
            inputType={component.name === 'text-input' ? 'text' : 
                      component.name === 'number-input' ? 'number' : 'email'}
            formTemplateId={formTemplateId}
            onThresholdIssueRaised={handleThresholdIssueRaised}
            raisedThresholdIssues={raisedThresholdIssues}
          />
        )
      
      case 'textarea':
        return <DfFormTextarea {...commonProps} properties={component as any} />
      
      case 'select':
        // Map basic.options to top-level options for select component
        const selectProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormSelect {...commonProps} properties={selectProps as any} />
      
      case 'checkbox':
        // Map basic.options to top-level options for checkbox component
        const checkboxProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }

        return <DfFormCheckbox {...commonProps} properties={checkboxProps as any} />
      
      case 'radio':
        // Map basic.options to top-level options for radio component
        const radioProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormRadio {...commonProps} properties={radioProps as any} />
      
      case 'segment':
        // Map basic.options to top-level options for segment component
        const segmentProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormSegment {...commonProps} properties={segmentProps as any} />
      
      case 'date':
      case 'date-picker':
        return <DfFormDateTime {...commonProps} properties={component as any} />
      
      case 'datetime-picker':
        return <DfFormDateTime {...commonProps} properties={component as any} />
      
      case 'signature':
        return <DfFormSignature {...commonProps} properties={component as any} />
      
      case 'heading':
        return (
          <DfFormHeading
            {...commonProps}
            properties={component as any}
          />
        )
      
      case 'instructions':
        // CRITICAL: Pass formValue to instructions component so it can display API data
        return (
          <DfFormInstruction
            {...commonProps}
            properties={component as any}
            formValue={formValue}
          />
        )
      
      case 'location':
        return <DfFormLocation {...commonProps} properties={component as any} />
      
      case 'section':
        return (
          <DfFormSection
            {...commonProps}
            properties={component as any}
            formData={formValues}
            formTemplateId={formTemplateId}
            onThresholdActionCompletion={handleThresholdActionCompletion}
            onThresholdIssueRaised={handleThresholdIssueRaised}
            renderFormComponent={(field: FormComponentType) => {
              // Ensure the nested component gets the proper form value
              // const fieldValue = formValues[field.id] || (field.basic as any)?.value || (field.basic as any)?.defaultValue || ''
              return renderFormComponent(field)
            }}
          />
        )
      
      case 'table':

        return (
          <React.Suspense fallback={<div>Loading table...</div>}>
            <DfFormTable 
              {...commonProps} 
              properties={component as any}
              formData={formValues}
              formTemplateId={formTemplateId}
              mode={commonProps.mode}
              validationErrors={validationErrors}
              touchedFields={touchedFields}
              formSubmitted={formSubmitted}
              onThresholdActionCompletion={handleThresholdActionCompletion}
              onThresholdIssueRaised={handleThresholdIssueRaised}
              onNotesChange={(componentId: string, notes: string) => {
                // Handle notes change for table cell components
                const updatedComponents = formComponents.map(comp => {
                  if (comp.id === component.id && (comp as any).cells) {
                    const updatedCells = (comp as any).cells.map((row: any[]) =>
                      row.map((cell: any) => {
                        if (cell.components) {
                          const updatedCellComponents = cell.components.map((cellComp: any) => {
                            if (cellComp.id === componentId) {
                              return {
                                ...cellComp,
                                basic: {
                                  ...cellComp.basic,
                                  notes
                                }
                              }
                            }
                            return cellComp
                          })
                          return { ...cell, components: updatedCellComponents }
                        }
                        return cell
                      })
                    )
                    return { ...comp, cells: updatedCells }
                  }
                  return comp
                })
                onFormDataChange?.(updatedComponents)
              }}
              onAttachmentChange={(componentId: string, attachments: File[] | null) => {
                // Handle attachment change for table cell components
                const updatedComponents = formComponents.map(comp => {
                  if (comp.id === component.id && (comp as any).cells) {
                    const updatedCells = (comp as any).cells.map((row: any[]) =>
                      row.map((cell: any) => {
                        if (cell.components) {
                          const updatedCellComponents = cell.components.map((cellComp: any) => {
                            if (cellComp.id === componentId) {
                              return {
                                ...cellComp,
                                basic: {
                                  ...cellComp.basic,
                                  attachments: attachments || []
                                }
                              }
                            }
                            return cellComp
                          })
                          return { ...cell, components: updatedCellComponents }
                        }
                        return cell
                      })
                    )
                    return { ...comp, cells: updatedCells }
                  }
                  return comp
                })
                onFormDataChange?.(updatedComponents)
              }}
              renderFormComponent={(field: FormComponentType) => {
                // Ensure the nested component gets the proper form value
                return renderFormComponent(field)
              }}
            />
          </React.Suspense>
        )
      
      case 'datagrid':
        return (
          <DfFormDataGrid
            {...commonProps}
            properties={component as any}
            formData={formValues}
            formTemplateId={formTemplateId}
            mode={commonProps.mode}
            validationErrors={validationErrors}
            touchedFields={touchedFields}
            formSubmitted={formSubmitted}
            onThresholdActionCompletion={handleThresholdActionCompletion}
            onThresholdIssueRaised={handleThresholdIssueRaised}
            onComponentSelect={onComponentSelect}
            onComponentDelete={onComponentDelete}
            onComponentEdit={onComponentEdit}
            onComponentUpdate={onComponentUpdate}
            selectedComponent={selectedComponent}
            onValueChange={(change: IFormControlChange) => {
              // Handle datagrid value changes (entries updates)
              if (change.id === component.id && change.value && typeof change.value === 'object' && 'entries' in change.value) {
                const updatedComponents = formComponents.map(comp => {
                  if (comp.id === component.id) {
                    return {
                      ...comp,
                      ...change.value
                    } as FormComponentType
                  }
                  return comp
                })
                onFormDataChange?.(updatedComponents)
              } else {
                // For nested component value changes, use the regular handler
                onFormValueChange(change)
              }
            }}
            onEntryAdd={() => {
              // Handle entry add - update form components
              // Note: The entry has already been added via onValueChange in DfFormDataGrid
              // This callback is just to sync formComponents if needed
              try {
                const updatedComponents = formComponents.map(comp => {
                  if (comp.id === component.id) {
                    // Get the latest entries from the component (they should already be updated via onValueChange)
                    const currentEntries = (comp as any).entries || []
                    // Just ensure formComponents is in sync - the entry should already be there
                    return {
                      ...comp,
                      entries: currentEntries // Use the already-updated entries
                    }
                  }
                  return comp
                })
                onFormDataChange?.(updatedComponents)
              } catch (error) {
                console.error('[DfFormPreview] Error in onEntryAdd:', error);
              }
            }}
            onEntryRemove={(entryIndex: number) => {
              // Handle entry remove - update form components
              const updatedComponents = formComponents.map(comp => {
                if (comp.id === component.id && (comp as any).entries) {
                  const currentEntries = (comp as any).entries || []
                  const updatedEntries = currentEntries
                    .filter((_: any, index: number) => index !== entryIndex)
                    .map((entry: any, index: number) => ({
                      ...entry,
                      index,
                      id: `entry-${comp.id}-${index}`,
                      components: entry.components?.map((comp: any, compIndex: number) => {
                        const templateComp = ((comp as any).templateComponents || [])[compIndex]
                        return {
                          ...comp,
                          id: templateComp ? `${templateComp.id}-entry-${index}-${compIndex}` : comp.id
                        }
                      }) || []
                    }))
                  return {
                    ...comp,
                    entries: updatedEntries
                  }
                }
                return comp
              })
              onFormDataChange?.(updatedComponents)
            }}
            onNotesChange={(componentId: string, notes: string) => {
              // Handle notes change for datagrid entry components
              const updatedComponents = formComponents.map(comp => {
                if (comp.id === component.id && (comp as any).entries) {
                  const updatedEntries = (comp as any).entries.map((entry: any) => {
                    if (entry.components) {
                      const updatedComponents = entry.components.map((entryComp: any) => {
                        if (entryComp.id === componentId) {
                          return {
                            ...entryComp,
                            basic: {
                              ...entryComp.basic,
                              notes
                            }
                          }
                        }
                        return entryComp
                      })
                      return { ...entry, components: updatedComponents }
                    }
                    return entry
                  })
                  return { ...comp, entries: updatedEntries }
                }
                return comp
              })
              onFormDataChange?.(updatedComponents)
            }}
            onAttachmentChange={(componentId: string, attachments: File[] | null) => {
              // Handle attachment change for datagrid entry components
              const updatedComponents = formComponents.map(comp => {
                if (comp.id === component.id && (comp as any).entries) {
                  const updatedEntries = (comp as any).entries.map((entry: any) => {
                    if (entry.components) {
                      const updatedComponents = entry.components.map((entryComp: any) => {
                        if (entryComp.id === componentId) {
                          return {
                            ...entryComp,
                            basic: {
                              ...entryComp.basic,
                              attachments: attachments || []
                            }
                          }
                        }
                        return entryComp
                      })
                      return { ...entry, components: updatedComponents }
                    }
                    return entry
                  })
                  return { ...comp, entries: updatedEntries }
                }
                return comp
              })
              onFormDataChange?.(updatedComponents)
            }}
            renderFormComponent={(field: FormComponentType) => {
              // Ensure the nested component gets the proper form value
              return renderFormComponent(field)
            }}
          />
        )
      
      case 'file':
        // Get file value from formValues, or from component basic properties
        const fileFormValue = formValue || 
          (component.basic as any)?.files || 
          (component.basic as any)?.attachments || 
          (component.basic as any)?.value || 
          null;
        return (
          <DfFormFileUpload 
            {...commonProps} 
            properties={component as any}
            formValue={fileFormValue}
          />
        )
    
      
      default:
        return (
          <div className="form-group">
            <div className="form-group-label">
              Unsupported Component: {component.name}
            </div>
          </div>
        )
    }
  }

  return (
    <div className="form-preview-container">
      <div className="form-preview-wrapper">
        <div className={`form-preview ${Object.entries(getPreviewClasses()).map(([key, value]: [string, boolean]) => value ? key : '').join(' ')}`}>
          {/* Form Canvas Header - You can implement this separately */}
          <div className="form-canvas-header">
            {/* <FormCanvasHeader 
              formData={formData}
              disableSaveDraft={false}
              disableCancel={false}
              disableSubmit={false}
              showTags={false}
            /> */}
          </div>

          {formComponents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-card">
                <div className="empty-state-preview-area">
                  <span className="empty-state-placeholder">
                    Add components to see the form preview
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <form className="form-preview-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              {/* Form Title and Description */}
              {(formTitle || formDescription) && (
                <div className="form-header">
                  {formTitle && (
                    <h1 className="form-title">{formTitle}</h1>
                  )}
                  {formDescription && (
                    <p className="form-description">{formDescription}</p>
                  )}
                </div>
              )}

              {formComponents.map((component) => {
                const basic = component.basic as any;
                const formValue = formValues[component.id];
                const isOptionBased = ['select', 'radio', 'checkbox', 'segment'].includes(component.name || '');
                const componentOptions = (component as any).options || basic?.options || [];
                
                // Get column class from component styles
                const getColumnClass = (): string => {
                  const column = (component as any)?.styles?.column;
                  // Section and table components should always be full width
                  if (component.name === 'section' || component.name === 'table' || component.name === 'datagrid') {
                    return 'col-12';
                  }
                  // Map column value to class name
                  if (column === 3) return 'col-3';
                  if (column === 6) return 'col-6';
                  if (column === 9) return 'col-9';
                  if (column === 12) return 'col-12';
                  // Default to full width if no column specified
                  return 'col-12';
                };
                
                const columnClass = getColumnClass();
                
                // Check if component or selected option has action features
                let hasActionFeatures = false;
                if (!isPreviewMode) {
                  if (isOptionBased && formValue !== undefined && formValue !== null && formValue !== '') {
                    if (component.name === 'checkbox') {
                      const selectedValues = Array.isArray(formValue) ? formValue : [formValue];
                      hasActionFeatures = selectedValues.some((value: string) => {
                        const option = componentOptions.find((opt: any) => opt.value === value);
                        return option && (option.enableNotes || option.enableAttachment || option.enableRaiseIssue || option.enableSendEmail);
                      });
                    } else {
                      const selectedOption = componentOptions.find((opt: any) => opt.value === formValue);
                      hasActionFeatures = selectedOption && (selectedOption.enableNotes || selectedOption.enableAttachment || selectedOption.enableRaiseIssue || selectedOption.enableSendEmail);
                    }
                  } else {
                    hasActionFeatures = basic?.enableNotes || basic?.enableAttachment || basic?.enableRaiseIssue || basic?.enableSendEmail;
                  }
                }
                
                // Check if component has notes or attachments for submission view
                const hasSubmissionData = isPreviewMode && (
                  ((component as any).basic?.notes && (component as any).basic.notes.trim().length > 0) ||
                  ((component as any).basic?.attachments && Array.isArray((component as any).basic.attachments) && (component as any).basic.attachments.length > 0)
                );

                return (
                  <div 
                    key={component.id} 
                    className={`form-group ${columnClass} ${hasActionFeatures || hasSubmissionData ? 'has-action-features' : ''}`}
                    style={{ display: shouldShowComponent(component.id) ? 'block' : 'none' }}
                  >
                    {renderFormComponent(component)}
                    {/* Action Features - Only show in test mode, exclude certain component types */}
                    {!isPreviewMode && !['section', 'table', 'heading', 'file', 'instructions', 'signature', 'location', 'datagrid'].includes(component.name) && (
                      <ComponentActionFeatures
                        component={component as any}
                        mode={isPreviewMode ? 'preview' : 'test'}
                        formTemplateId={formTemplateId}
                        formValue={formValue}
                        onThresholdActionCompletion={handleThresholdActionCompletion}
                        onThresholdIssueRaised={handleThresholdIssueRaised}
                        onNotesChange={(notes: string) => {
                          // Handle notes change for regular components - update component.basic.notes
                          const updatedComponents = formComponents.map(comp => {
                            if (comp.id === component.id) {
                              return {
                                ...comp,
                                basic: {
                                  ...comp.basic,
                                  notes: notes || ''
                                } as any
                              } as FormComponentType
                            }
                            return comp
                          })
                          onFormDataChange?.(updatedComponents as FormComponentType[])
                        }}
                        onAttachmentChange={(attachments: File[] | null) => {
                          // Handle attachment change for regular components - update component.basic.attachments
                          const updatedComponents = formComponents.map(comp => {
                            if (comp.id === component.id) {
                              return {
                                ...comp,
                                basic: {
                                  ...comp.basic,
                                  attachments: attachments || []
                                } as any
                              } as FormComponentType
                            }
                            return comp
                          })
                          onFormDataChange?.(updatedComponents as FormComponentType[])
                        }}
                        notes={(component as any).basic?.notes || ''}
                        attachments={(component as any).basic?.attachments || null}
                      />
                    )}
                    {/* Submission Actions - Show in preview mode when there are notes or attachments */}
                    {isPreviewMode && hasSubmissionData && (
                      <ComponentSubmissionActions
                        component={component as any}
                      />
                    )}
                  </div>
                );
              })}
              
              {!isPreviewMode && (
                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={!isFormValid() || !thresholdValidationState.isValid}
                    className="form-submit-button"
                    title={!thresholdValidationState.isValid ? thresholdValidationState.errorMessage : ''}
                  >
                    Submit
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default DfFormPreview
